import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  es: {
    // Navigation
    dashboard: 'Dashboard',
    brands: 'Marcas',
    generator: 'Generador',
    library: 'Biblioteca',
    teams: 'Equipos',
    settings: 'Configuración',
    logout: 'Cerrar Sesión',
    
    // Landing
    heroTitle: 'Potencia tu creatividad con IA',
    heroSubtitle: 'Genera ideas, copys y prompts de imagen para todas tus campañas de marketing',
    getStarted: 'Comenzar Ahora',
    loginWithGoogle: 'Iniciar con Google',
    
    // Dashboard
    welcome: 'Bienvenido',
    totalBrands: 'Marcas Totales',
    totalIdeas: 'Ideas Generadas',
    favorites: 'Favoritos',
    recentActivity: 'Actividad Reciente',
    ideasThisWeek: 'Ideas esta semana',
    
    // Brands
    myBrands: 'Mis Marcas',
    addBrand: 'Agregar Marca',
    editBrand: 'Editar Marca',
    brandName: 'Nombre de la Marca',
    target: 'Target/Audiencia',
    brandPromise: 'Promesa de Marca',
    services: 'Servicios/Productos',
    slogan: 'Slogan',
    tone: 'Tono de Comunicación',
    keywords: 'Palabras Clave',
    extraInfo: 'Información Adicional',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    
    // Generator
    generateCopy: 'Generar Copy',
    selectBrand: 'Seleccionar Marca',
    selectMedium: 'Medio',
    topic: 'Tema/Producto',
    quantity: 'Cantidad',
    aiModel: 'Modelo de IA',
    generate: 'Generar',
    generating: 'Generando...',
    
    // Media types
    radio: 'Radio',
    tv: 'Televisión',
    reel: 'Reel',
    story: 'Story',
    post: 'Post',
    
    // Copy structure
    hook: 'Hook/Problema',
    solution: 'Solución',
    benefit: 'Beneficio',
    closing: 'Cierre Institucional',
    fullCopy: 'Copy Completo',
    imagePrompt: 'Prompt de Imagen',
    
    // Actions
    copyToClipboard: 'Copiar',
    copied: 'Copiado!',
    addToFavorites: 'Agregar a Favoritos',
    removeFromFavorites: 'Quitar de Favoritos',
    exportPdf: 'Exportar PDF',
    share: 'Compartir',
    
    // Library
    myLibrary: 'Mi Biblioteca',
    allIdeas: 'Todas',
    favoritesOnly: 'Favoritos',
    filterByBrand: 'Filtrar por Marca',
    noIdeas: 'No hay ideas aún. ¡Genera tu primera idea!',
    
    // Teams
    myTeams: 'Mis Equipos',
    createTeam: 'Crear Equipo',
    teamName: 'Nombre del Equipo',
    inviteMember: 'Invitar Miembro',
    email: 'Correo Electrónico',
    members: 'Miembros',
    owner: 'Propietario',
    
    // Settings
    language: 'Idioma',
    spanish: 'Español',
    english: 'English',
    profile: 'Perfil',
    
    // Misc
    loading: 'Cargando...',
    error: 'Error',
    success: 'Éxito',
    confirm: 'Confirmar',
    noBrands: 'No tienes marcas. ¡Crea tu primera marca!',
    createFirst: 'Crear primera marca',
  },
  en: {
    // Navigation
    dashboard: 'Dashboard',
    brands: 'Brands',
    generator: 'Generator',
    library: 'Library',
    teams: 'Teams',
    settings: 'Settings',
    logout: 'Logout',
    
    // Landing
    heroTitle: 'Power Your Creativity with AI',
    heroSubtitle: 'Generate ideas, copies and image prompts for all your marketing campaigns',
    getStarted: 'Get Started',
    loginWithGoogle: 'Login with Google',
    
    // Dashboard
    welcome: 'Welcome',
    totalBrands: 'Total Brands',
    totalIdeas: 'Ideas Generated',
    favorites: 'Favorites',
    recentActivity: 'Recent Activity',
    ideasThisWeek: 'Ideas this week',
    
    // Brands
    myBrands: 'My Brands',
    addBrand: 'Add Brand',
    editBrand: 'Edit Brand',
    brandName: 'Brand Name',
    target: 'Target/Audience',
    brandPromise: 'Brand Promise',
    services: 'Services/Products',
    slogan: 'Slogan',
    tone: 'Communication Tone',
    keywords: 'Keywords',
    extraInfo: 'Additional Information',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    
    // Generator
    generateCopy: 'Generate Copy',
    selectBrand: 'Select Brand',
    selectMedium: 'Medium',
    topic: 'Topic/Product',
    quantity: 'Quantity',
    aiModel: 'AI Model',
    generate: 'Generate',
    generating: 'Generating...',
    
    // Media types
    radio: 'Radio',
    tv: 'Television',
    reel: 'Reel',
    story: 'Story',
    post: 'Post',
    
    // Copy structure
    hook: 'Hook/Problem',
    solution: 'Solution',
    benefit: 'Benefit',
    closing: 'Institutional Closing',
    fullCopy: 'Full Copy',
    imagePrompt: 'Image Prompt',
    
    // Actions
    copyToClipboard: 'Copy',
    copied: 'Copied!',
    addToFavorites: 'Add to Favorites',
    removeFromFavorites: 'Remove from Favorites',
    exportPdf: 'Export PDF',
    share: 'Share',
    
    // Library
    myLibrary: 'My Library',
    allIdeas: 'All',
    favoritesOnly: 'Favorites',
    filterByBrand: 'Filter by Brand',
    noIdeas: 'No ideas yet. Generate your first idea!',
    
    // Teams
    myTeams: 'My Teams',
    createTeam: 'Create Team',
    teamName: 'Team Name',
    inviteMember: 'Invite Member',
    email: 'Email',
    members: 'Members',
    owner: 'Owner',
    
    // Settings
    language: 'Language',
    spanish: 'Español',
    english: 'English',
    profile: 'Profile',
    
    // Misc
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    confirm: 'Confirm',
    noBrands: 'You have no brands. Create your first brand!',
    createFirst: 'Create first brand',
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('es');
  
  useEffect(() => {
    const saved = localStorage.getItem('braingl_language');
    if (saved) setLanguage(saved);
  }, []);
  
  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('braingl_language', lang);
  };
  
  const t = (key) => translations[language][key] || key;
  
  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
